/**
 * 랜딩 페이지
 * - 비로그인 사용자에게 보여지는 마케팅 페이지
 * - 주요 기능, 사용 방법, 요금제 미리보기 포함
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { useSEO } from '../hooks/useSEO';
import ShareButtons from '../components/ui/ShareButtons';
import { Palette, Package, Cloud } from 'lucide-react';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useSEO({ title: 'MD Label Maker', description: '미니디스크 라벨을 쉽고 아름답게 제작하세요.' });

  const features = [
    {
      icon: <Palette size={36} />,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
    },
    {
      icon: <Package size={36} />,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
    },
    {
      icon: <Cloud size={36} />,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
    },
  ];

  const steps = [
    {
      title: t('landing.step1Title'),
      description: t('landing.step1Desc'),
    },
    {
      title: t('landing.step2Title'),
      description: t('landing.step2Desc'),
    },
    {
      title: t('landing.step3Title'),
      description: t('landing.step3Desc'),
    },
  ];

  return (
    <div className="min-h-screen">
      {/* 히어로 섹션 */}
      <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white py-12 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl xs:text-3xl md:text-5xl lg:text-6xl font-bold font-display mb-6 whitespace-pre-line break-words">
            {t('landing.heroTitle')}
          </h1>
          <p className="text-sm xs:text-base md:text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('landing.heroDesc')}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/signup')}
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-semibold rounded-xl h-auto"
            >
              {t('landing.startFree')}
            </Button>
            <Button
              onClick={() => navigate('/demo')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-3 text-lg rounded-xl h-auto bg-transparent"
            >
              {t('landing.demoCTA')}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="border-white/60 text-white/80 hover:bg-white/10 px-6 py-3 text-base rounded-xl h-auto bg-transparent"
            >
              {t('common.login')}
            </Button>
          </div>
          <p className="text-white/70 text-sm mt-3">{t('landing.demoDesc')}</p>
        </div>
      </section>

      {/* 주요 기능 섹션 */}
      <section className="py-10 sm:py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8 md:mb-12">{t('landing.features')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="text-center p-6 rounded-2xl bg-gray-50 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4 text-blue-600" aria-hidden="true">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 사용 방법 섹션 */}
      <section className="py-10 sm:py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8 md:mb-12">{t('landing.howToUse')}</h2>
          <div className="space-y-8">
            {steps.map((step, i) => (
              <div key={step.title} className="flex items-start gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 요금제 미리보기 섹션 */}
      <section className="py-10 sm:py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 md:mb-12">{t('landing.pricingTitle')}</h2>
          <p className="text-gray-600 mb-8">{t('landing.pricingDesc')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50">
              <h3 className="font-bold text-lg mb-1">Free</h3>
              <p className="text-3xl font-extrabold mb-2">₩0</p>
              <p className="text-sm text-gray-700">앨범 5개</p>
            </div>
            <div className="p-6 rounded-2xl border-2 border-blue-500 bg-blue-50 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
                {t('pricing.recommended')}
              </span>
              <h3 className="font-bold text-lg mb-1">Basic</h3>
              <p className="text-3xl font-extrabold mb-2">
                ₩3,900<span className="text-sm font-normal text-gray-700">{t('pricing.perMonth')}</span>
              </p>
              <p className="text-sm text-gray-700">앨범 30개</p>
            </div>
            <div className="p-6 rounded-2xl border border-gray-200 bg-gray-50">
              <h3 className="font-bold text-lg mb-1">Premium</h3>
              <p className="text-3xl font-extrabold mb-2">
                ₩7,900<span className="text-sm font-normal text-gray-700">{t('pricing.perMonth')}</span>
              </p>
              <p className="text-sm text-gray-700">{t('pricing.unlimited')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/pricing')}
            variant="outline"
            className="mt-8"
          >
            {t('landing.viewPricing')}
          </Button>
        </div>
      </section>

      {/* 푸터 CTA 섹션 */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 md:mb-12">{t('landing.ctaTitle')}</h2>
          <p className="text-blue-100 mb-8">{t('landing.ctaDesc')}</p>
          <Button
            onClick={() => navigate('/signup')}
            className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-semibold rounded-xl h-auto"
          >
            {t('landing.freeSignup')}
          </Button>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="py-6 px-4 bg-gray-900 text-gray-400 text-center text-sm">
        <p>{t('landing.copyright', { year: new Date().getFullYear() })}</p>
        <div className="mt-4 flex justify-center">
          <ShareButtons
            url="https://md.endline.co.kr"
            title="MD Label Maker - 미니디스크 라벨 디자이너"
          />
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
